import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { personalInfoSchema } from "../schemas";
import { Form } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { StylesConfig, CSSObjectWithLabel, GroupBase } from "react-select";
import { City, Country } from "country-state-city";

interface CityData {
  name?: string;
  countryCode?: string;
}

interface PersonalInfoData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  city: string;
  personalPositionCompany: string;
  businessName: string;
  referralCodeUsed?: string;
}

interface PersonalInfoStepProps {
  defaultValues: PersonalInfoData;
  onNext: (data: PersonalInfoData) => void;
}

interface CityOption {
  value: string;
  label: string;
  city: string;
  country: string;
}

type SelectStyles = StylesConfig<CityOption, false, GroupBase<CityOption>>;

let citySearchTimeout: NodeJS.Timeout | null = null;

// Load city options - searches ALL cities across ALL countries (no link to phone number)
const loadCityOptions = (
  inputValue: string,
  callback: (options: readonly CityOption[]) => void
): void => {
  if (!inputValue || inputValue.trim().length === 0) {
    callback([]);
    return;
  }

  if (citySearchTimeout) {
    clearTimeout(citySearchTimeout);
  }

  citySearchTimeout = setTimeout(() => {
    try {
      const searchTerm = inputValue.trim().toLowerCase();
      const allCitiesData = City.getAllCities();
      const countryMap = new Map(
        Country.getAllCountries().map((c) => [c.isoCode, c.name])
      );

      const filtered: CityOption[] = allCitiesData
        .filter((city: CityData) => {
          const cityName = city.name?.toLowerCase() || "";
          return cityName.includes(searchTerm);
        })
        .slice(0, 30)
        .map((city: CityData) => {
          const countryName = countryMap.get(city.countryCode || "") || city.countryCode || "";
          const cityName = city.name || "";
          return {
            value: cityName,
            label: `${cityName}, ${countryName}`,
            city: cityName,
            country: countryName,
          };
        });

      callback(filtered);
    } catch (error) {
      console.error("Error searching cities:", error);
      callback([]);
    }
  }, 200);
};

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  defaultValues,
  onNext,
}) => {
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonalInfoData>({
    resolver: yupResolver(personalInfoSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = (data: PersonalInfoData) => {
    const formattedData = {
      ...data,
    };
    onNext(formattedData);
  };

  const customSelectStyles: SelectStyles = {
    control: (
      base: CSSObjectWithLabel,
      state: { isFocused: boolean }
    ): CSSObjectWithLabel => ({
      ...base,
      borderColor: state.isFocused
        ? "#86b7fe"
        : errors.city
        ? "#dc3545"
        : base.borderColor || "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : errors.city
        ? "0 0 0 0.2rem rgba(220,53,69,.25)"
        : base.boxShadow || "none",
      "&:hover": {
        borderColor: state.isFocused
          ? "#86b7fe"
          : errors.city
          ? "#dc3545"
          : base.borderColor || "#ced4da",
      },
      borderWidth: "1px",
      borderRadius: "0.375rem",
      minHeight: "38px",
      fontSize: "0.875rem",
    }),
    menu: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <h5 className="pb-2 d-block d-md-none">Personal Information</h5>
      <div className="row g-2">
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              First Name <span>*</span>
            </label>
          </div>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder="First Name"
              />
            )}
          />
          {errors.firstName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              Last Name <span>*</span>
            </label>
          </div>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder="Last Name"
              />
            )}
          />
          {errors.lastName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.lastName.message}
            </p>
          )}
        </div>
        <div className="col-12 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              Email Address <span>*</span>
            </label>
          </div>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="email"
                className="form-control"
                placeholder="Email Address"
                readOnly
              />
            )}
          />
          {errors.email && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.email.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              Phone Number <span>*</span>
            </label>
          </div>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <div className="form-control p-0">
                <PhoneInput
                  {...field}
                  placeholder="Enter phone number"
                  defaultCountry="GB"
                  value={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  className="phone-input w-100"
                />
              </div>
            )}
          />
          {errors.phoneNumber && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.phoneNumber.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              City <span>*</span>
            </label>
          </div>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                loadOptions={(inputValue, callback) => {
                  loadCityOptions(inputValue, callback);
                }}
                placeholder="Search and select city"
                isClearable
                styles={customSelectStyles}
                classNamePrefix="react-select"
                value={
                  field.value
                    ? { value: field.value, label: field.value, city: field.value, country: "" }
                    : null
                }
                onChange={(selected: CityOption | null) => {
                  field.onChange(selected ? selected.value : "");
                }}
                defaultOptions={false}
                noOptionsMessage={({ inputValue }) => {
                  if (!inputValue || inputValue.trim().length === 0) {
                    return "Start typing to search cities";
                  }
                  return "No cities found";
                }}
                loadingMessage={() => "Searching cities..."}
                cacheOptions
                isSearchable
                filterOption={null}
              />
            )}
          />
          {errors.city && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.city.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              Job Title <span>*</span>
            </label>
          </div>
          <Controller
            name="personalPositionCompany"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder="Enter your job title"
              />
            )}
          />
          {errors.personalPositionCompany && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.personalPositionCompany.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              Company Name <span>*</span>
            </label>
          </div>
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder="Enter your company name"
              />
            )}
          />
          {errors.businessName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.businessName.message}
            </p>
          )}
        </div>
      </div>

      {/* Discount code section */}
      <div className="mt-3">
        {!showDiscountInput ? (
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
            onClick={() => setShowDiscountInput(true)}
            style={{ color: "inherit", textDecoration: "underline", cursor: "pointer" }}
          >
            Have a Referral Code?
          </button>
        ) : (
          <Form.Group>
            <Form.Label>Referral Code</Form.Label>
            <Controller
              name="referralCodeUsed"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter referral code"
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
              )}
            />
          </Form.Group>
        )}
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button type="submit" className="btn btn-primary">
          Next
        </button>
      </div>
    </form>
  );
};

export default PersonalInfoStep;